// [목적] 모바일 /folio/:id — 컬러 커버 + 액션 + 상단 탭(Day/Map/Mood/Budget) + 하단 AI 보정 입력
// [이유] W5-3(design-alignment): FolioPage.tsx(W5-1)의 PLAN_ITEM_EMOJI_MAP/getItemEmoji 재사용하여
//         타임라인 아이콘을 타입약자(ST/PL 등)에서 emoji 로 변경. 마커 번호 01→1 정합.
//         W2(design-alignment): c.surface.*(쿨톤) → c.paper.*(warm), c.text.*(쿨톤) → c.ink.*(warm).
//         fe-be-gap-tier2 T14: BudgetTab → folio.budgetBreakdown 실데이터 우선, 부재 시 BUDGET mock 폴백.
//         fe-be-gap-tier2 T15: 커버 작성자 author.handle 우선 표시(@slug 폴백), viewCount 표시.
//         folio-generation-quality T13: DayTab 아이템을 4밴드(오전/점심/오후/저녁) 그룹 타임라인으로 렌더.
//         folio-generation-quality T15: BudgetTab 0원 숨김·인당 약 N만원·만원 표기 정합(ADR-FGQ-OQ5/6).
//         folio-generation-quality gap: groupMobilePlanItemsByBand 로컬 헬퍼 → timeBand.groupPlanItemsByTimeBand 통일.
//           planItemSchema.timeOfDay 정식 추가로 타입 캐스팅 없이 item.timeOfDay 우선 사용.
//         지도 SVG 미니맵/포토 무드보드는 §2 Out-of-scope — 현 placeholder 유지.
//         fe-folio-social-wire: 좋아요(♥)/Fork 로컬 useState(liked/forked) 를 useFolioLike/useForkFolio
//           실 API 훅으로 교체. 북마크 버튼 신규 추가(useFolioBookmark). "댓글" 5번째 탭 추가(CommentSection 공용).
// [사이드 이팩트] getItemEmoji import 추가. TimelineItem 컴포넌트 아이콘 렌더 변경.
//                 BudgetTab: BUDGET_CATEGORY_COLORS/BUDGET_CATEGORY_LABEL/formatKrw 를
//                 FolioPage.tsx / FolioBudgetPreview 에서 import(중복 방지) — FolioPage.tsx 가 SoT.
//                 BAND_META·groupPlanItemsByTimeBand import 교체(BAND_ORDER·groupItemsByBand 미사용 제거).
//                 groupMobilePlanItemsByBand 로컬 헬퍼 제거.
//                 liked/forked useState 제거 → useFolioLike/useForkFolio 훅 상태로 대체(캐시 공유).
// [수정 시 고려사항] PLAN_ITEM_EMOJI_MAP/getItemEmoji 변경 시 FolioPage.tsx 와 동기 필수.
//                   PlanItem.timeOfDay 는 planItemSchema 정식 필드 — 타입 캐스팅 불필요.
//                   Map SDK 실연결은 후속 map-integration Spec, MoodBoard 사진은 placeholder.
//                   WC-TOURAPI-QR-5-02/05 — "조용한" Quick Chip + VITE_FEATURE_QUIET_CHIP feature flag 추가.
//                   WC-TOURAPI-QR-5-03 — CongestionForecastCard DayTab 하단 부착 (poiId 연동 후속).
//                   pages/auth/** 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.
//                   좋아요/북마크는 새로고침 시 리셋됨(BE isLiked/isBookmarked 미제공, FolioPage.tsx 헤더 주석 참조).
//         FlowLine(2026-09-17) A1/A2/A3/A4/A5/A6/A7/A8/A9 — 공유/PDF/예산실데이터/지도 SDK/무드 실사진/
//           날씨 강수확률/신고/Day 가져오기/소유자 전용 편집 버튼을 모바일 뷰에도 배선(데스크톱과 동일 컴포넌트 재사용).
//         FlowLine dcc2c3cc FE 과업1 — 하단 "AI 보정 바"를 정적 placeholder(전송 버튼 미배선)에서
//           RefineInputBar+useFolioRefine+RefineProposalDialog 로 실배선. isOwner && days.length>0 일 때만
//           노출(비소유자에게는 편집 권한이 없어 BE 가 folio.NOT_FOUND 로 응답하므로 아예 숨김).
//           days.length>1 이면 대상 Day 선택 Select 를 함께 노출(데스크톱은 활성 Day 탭이 곧 대상이라 불필요).

import { useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { DayPlan, DayWeather, Folio, PlanItem } from '@gangwoncanvas/types';
import { useNavigate } from 'react-router-dom';

import { CongestionForecastCard } from '../../components/folio/CongestionForecastCard.js';
import { FolioCommentSection } from '../../components/folio/CommentSection.js';
import { DayImportDialog } from '../../components/folio/DayImportDialog.js';
import { FolioExportButton } from '../../components/folio/FolioExportButton.js';
import { FolioMap } from '../../components/folio/FolioMap.js';
import { FolioShareButton } from '../../components/folio/FolioShareButton.js';
import { formatKrw } from '../../components/folio/preview/FolioBudgetPreview.js';
import { RefineInputBar } from '../../components/folio/refine/RefineInputBar.js';
import { RefineProposalDialog } from '../../components/folio/refine/RefineProposalDialog.js';
import {
  BAND_META,
  formatForecastDate,
  groupPlanItemsByTimeBand,
} from '../../components/folio/timeBand.js';
import { LineIcon } from '../../components/icons/LineIcons.js';
import { ReportDialog } from '../../components/report/ReportDialog.js';
import { LoginPromptModal } from '../../components/auth/LoginPromptModal.js';
import { useFolioRefine } from '../../hooks/useFolioRefine.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useAuthGate } from '../../hooks/useAuthGate.js';
import { useForkFolio } from '../../hooks/useForkFolio.js';
import { useFolioLike } from '../../hooks/useFolioLike.js';
import { useFolioBookmark } from '../../hooks/useFolioBookmark.js';
import { usePoiCongestion } from '../../hooks/usePoiCongestion.js';
import { distributePercentages } from '../../utils/distributePercentages.js';
import { folioAuthorLabel } from '../../utils/folioAuthorLabel.js';
import {
  getItemEmoji,
  BUDGET_CATEGORY_COLORS,
  BUDGET_CATEGORY_LABEL,
  type BudgetCategoryKey,
} from '../FolioPage.js';

// [목적] paper/ink/line 정본 토큰 바인딩 — W2(design-alignment) 쿨톤 정본화
// [이유] ADR-TR8: c.text.*(쿨톤) → c.ink.*(warm), c.surface.*(쿨톤) → c.paper.*(warm). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         이전 커밋(W1-3)에서 TEXT_LOW/BORDER/BG1 은 이미 ink.low/line.base/surface[1] 로 교체됨.
//         본 W2 에서 TEXT_HI/TEXT_MID 를 c.text.high/mid(쿨톤) → c.ink.high/mid(warm) 로 완성.
//         BG1: c.surface[1](쿨톤 #F6F8FB) → c.paper[1](warm #FAF9F7).
// [사이드 이팩트] 배경·텍스트 색 온도 warm 전환. 기존 c.line.base/c.ink.low 는 이미 정본.
// [수정 시 고려사항] ink.low(#6F6A5C)는 보조 텍스트 한정(본문 미사용, ADR-TR8). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
// [목적] 스크린리더 전용 텍스트(시각적으로는 숨김)
// [이유] 조회수 이모지를 LineIcon 으로 바꾸면서 아이콘(aria-hidden)만으로는 "조회수" 의미가 전달되지 않는다.
//        데스크톱 FolioPage.tsx 의 visuallyHiddenSx 와 동일 관용구 — 세 번째 사용처가 생기면 공용화할 것.
const MOBILE_VISUALLY_HIDDEN_SX = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L]; // #29261B (← 쿨톤 c.text.high #14181F)
const TEXT_MID = c.ink.mid[L]; // #5C5849 (← 쿨톤 c.text.mid #586069)
const TEXT_LOW = c.ink.low[L]; // #6F6A5C — 보조 텍스트 한정(ADR-TR8, 본문 미사용)
const BORDER = c.line.base[L]; // #E8E2D5 — 이미 정본 토큰
const BG1 = c.paper[1][L]; // #FAF9F7 (← 쿨톤 c.surface[1] #F6F8FB)
const BRAND = c.brand[500][L];
const GREEN = c.accent[500][L];
// [2026-09-18] 커버 헤더 액션 칩(뒤로/PDF/공유/편집/신고/좋아요/북마크/Fork) 배경 — 기존
// rgba(255,255,255,.25)(흰 반투명, 커버색을 밝힘)는 흰 텍스트 기준 3.07:1 로 AA 미달이었다
// (docs/qa/2026-09-17_contrast-measure.md). 검정 반투명 오버레이(overlay.scrimOnBrand)로 교체해
// 어떤 커버색 위에서도 상대적으로 어둡혀 흰 텍스트 대비를 확보한다(packages/ui color.ts 주석 참조).
const HEADER_CHIP_BG = c.overlay.scrimOnBrand[L];

// [목적] "조용한" 칩 전용 색상 토큰 참조 (FolioMobile)
// [이유] WC-TOURAPI-QR-5-01/02 — chip.quiet 토큰으로 기존 칩과 시각 구분
// [사이드 이팩트] 없음
// [수정 시 고려사항] 디자이너 R-8 검수 후 color.ts 토큰만 갱신하면 자동 반영
//                   [2026-09-18] QUIET_FG 추가 — 기존에 QUIET_BORDER(테두리 전용 설계값)를 비활성
//                   텍스트 색으로 재사용해 bg 위 3.3:1 로 AA 미달이었다. 텍스트는 QUIET_FG(6.55:1) 사용.
const QUIET_BG = c.chip.quiet.bg[L];
const QUIET_BORDER = c.chip.quiet.border[L];
const QUIET_FG = c.chip.quiet.fg[L];
const QUIET_BG_ACTIVE = c.chip.quiet.bgActive[L];
const QUIET_FG_ACTIVE = c.chip.quiet.fgActive[L];

// [목적] VITE_FEATURE_QUIET_CHIP feature flag 읽기
// [이유] WC-TOURAPI-QR-5-05 R-1 — TourAPI 활용 신청 미완료 대비 런타임 스위치
// [사이드 이팩트] .env 변경 후 Vite dev 서버 재시작 필요
// [수정 시 고려사항] 활용 신청 승인 후 .env 에 VITE_FEATURE_QUIET_CHIP=on 설정 시 즉시 활성화
const QUIET_CHIP_ENABLED = import.meta.env.VITE_FEATURE_QUIET_CHIP === 'on';

type TabId = 'day' | 'map' | 'mood' | 'budget' | 'comments';

// ─── MobileWeatherLabel ──────────────────────────────────────────────────────

/**
 * [목적] 모바일 Day 날씨 인라인 라벨 — weather.code → 아이콘/라벨 (fe-be-gap-tier1 T13, ADR-TR-6)
 * [이유] FolioPage.tsx 의 WeatherLabel 와 동일 로직 — 모바일 전용 컴포넌트로 분리.
 *         재사용성보다 파일 분리 명확성 우선. 날씨 코드 매핑은 양 파일에 동일하게 유지.
 * [사이드 이팩트] 없음 — 추가 UI 요소만.
 * [수정 시 고려사항] 매핑 테이블 변경 시 FolioPage.tsx 의 WEATHER_CODE_MAP 과 동기 갱신 필요.
 */
const MOBILE_WEATHER_CODE_MAP: Record<string, { icon: string; label: string }> = {
  sunny: { icon: '☀️', label: '맑음' },
  cloudy: { icon: '⛅', label: '구름 조금' },
  overcast: { icon: '☁️', label: '흐림' },
  rainy: { icon: '🌧', label: '비' },
  snowy: { icon: '❄️', label: '눈' },
  stormy: { icon: '⛈', label: '뇌우' },
  foggy: { icon: '🌫', label: '안개' },
  '1': { icon: '☀️', label: '맑음' },
  '3': { icon: '⛅', label: '구름 많음' },
  '4': { icon: '☁️', label: '흐림' },
};

function MobileWeatherLabel({ weather }: { weather: DayWeather }): JSX.Element {
  const mapped = weather.code
    ? (MOBILE_WEATHER_CODE_MAP[weather.code] ?? { icon: '🌤', label: weather.code })
    : null;
  const hasTemp = weather.tempMax !== null || weather.tempMin !== null;
  const hasPop = weather.pop != null;
  // [2026-09-21] 데스크톱 WeatherLabel 과 동일하게 예보 날짜를 "9월 21일(일)" 로 앞에 표기한다.
  //              포매터는 timeBand.ts 단일 소스 — 두 화면의 날짜 표기가 갈라지지 않게 한다.
  const dateLabel = formatForecastDate(weather.forecastDate);
  return (
    <Box
      component="span"
      aria-label={`${dateLabel ? `${dateLabel} ` : ''}날씨: ${mapped?.label ?? ''}${hasTemp ? ` 최고 ${weather.tempMax ?? '-'}°C` : ''}${hasPop ? ` 강수확률 ${weather.pop}%` : ''}`}
      sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 11, mb: '3px' }}
    >
      {dateLabel && (
        <Box
          component="span"
          title={
            weather.relative === true
              ? '여행 날짜가 정해지지 않아 오늘부터의 예보를 보여드려요'
              : undefined
          }
          sx={{
            color: TEXT_HI,
            bgcolor: c.paper[2][L],
            borderRadius: '6px',
            px: '4px',
            fontSize: 10.5,
            fontWeight: 600,
          }}
        >
          {dateLabel}
        </Box>
      )}
      {mapped && (
        <Box component="span" aria-hidden="true">
          {mapped.icon}
        </Box>
      )}
      {mapped && (
        <Box component="span" sx={{ color: TEXT_MID }}>
          {mapped.label}
        </Box>
      )}
      {hasTemp && (
        <Box
          component="span"
          sx={{
            color: TEXT_HI,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            fontWeight: 600,
          }}
        >
          {weather.tempMax !== null ? `${weather.tempMax}°C` : `${weather.tempMin}°C`}
        </Box>
      )}
      {/* FlowLine A6: 강수확률(pop) — 있을 때만 표시(후방호환) */}
      {hasPop && (
        <Box
          component="span"
          sx={{ color: TEXT_MID, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
        >
          강수 {weather.pop}%
        </Box>
      )}
    </Box>
  );
}

// [목적] 타임라인 아이템 — emoji 아이콘 + 마커 번호 W5-3 정합
// [이유] W5-3: 아이콘을 타입약자(ST/PL/ME 등)에서 emoji 로 변경(W5-1 매핑 재사용).
//         마커 번호를 padStart(2,'0') → 단순 숫자(01→1) 로 변경.
//         getItemEmoji(item): PlanItem.icon(미래 BE 필드) 우선, 없으면 PLAN_ITEM_EMOJI_MAP 폴백(R-4).
// [사이드 이팩트] 없음 (렌더 변경만)
// [수정 시 고려사항] PLAN_ITEM_EMOJI_MAP 변경 시 FolioPage.tsx 와 동기 필수.
function TimelineItem({ item, index }: { item: PlanItem; index: number }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', gap: '10px', mb: '10px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 36,
          flexShrink: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: 10,
            color: TEXT_LOW,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            whiteSpace: 'nowrap',
          }}
        >
          {index + 1}
        </Box>
        {/* [목적] 타임라인 세로 연결선 — 순번 뱃지 아래로 다음 아이템까지 이어지는 1px 선
            [이유] 2026-09-18 QA(H-2): `width: 1` 은 MUI sx 에서 퍼센트(100%)로 변환돼
                   부모 폭 36px 을 그대로 먹은 베이지 블록으로 렌더됐다. 단위를 명시해 1px 로 고정.
            [사이드 이팩트] 없음 — 시각 요소 전용
            [수정 시 고려사항] MUI sx 의 width/height 는 값이 1 이하면 퍼센트로 변환된다.
                               1px 선을 의도할 때는 반드시 문자열 '1px' 로 쓸 것 */}
        <Box sx={{ width: '1px', flex: 1, bgcolor: BORDER, mt: '4px' }} />
      </Box>
      <Box
        sx={{
          flex: 1,
          bgcolor: c.paper[0][L],
          borderRadius: '10px',
          p: '9px 11px',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 1px 3px rgba(41,38,27,.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '3px' }}>
          <Box component="span" aria-hidden="true" sx={{ fontSize: 16, flexShrink: 0 }}>
            {getItemEmoji(item)}
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_HI, flex: 1 }}>
            {item.title ?? '(이름 없음)'}
          </Typography>
        </Box>
        {item.note && (
          <Typography sx={{ fontSize: 11, color: TEXT_MID, mb: '4px' }}>{item.note}</Typography>
        )}
        {item.costEstimate > 0 && (
          <Typography
            sx={{
              fontSize: 11,
              color: BRAND,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              fontWeight: 500,
            }}
          >
            ₩{item.costEstimate.toLocaleString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * [목적] Day 탭 — 날짜 선택 + 밴드 타임라인 아이템 목록 + 혼잡도 예측 카드
 * [이유] T13: 기존 flat 아이템 렌더를 4밴드(오전/점심/오후/저녁) 그룹 타임라인으로 교체.
 *         folio-generation-quality gap: groupPlanItemsByTimeBand(timeBand.ts) 재사용 —
 *           PlanItem.timeOfDay(정식 추가) 우선, 없으면 startAt ISO 파생, 기본값 'afternoon'.
 *         WC-TOURAPI-QR-5-03 — 타임라인 아이템 클릭 시 해당 POI 혼잡도 카드 표시.
 * [사이드 이팩트] groupPlanItemsByTimeBand import 재사용. 로컬 groupMobilePlanItemsByBand 헬퍼 제거.
 * [수정 시 고려사항] [G2-2, 2026-09-17] PlanItem.poiId 는 항상 null 이라 factInfo.contentId(계약 C2,
 *                   TourAPI content_id 원문)를 GET /pois/:id/congestion 호출 키로 사용한다 — BE :id 파라미터가
 *                   실제로 content_id 다(poi-meta.controller.ts 주석 근거). factInfo 없으면 미표시(graceful).
 *                   빈 밴드 자동 숨김(groupPlanItemsByTimeBand 내부).
 *                   FlowLine A8: canImportDay=true 면 현재 선택된 Day 옆에 "가져오기" 버튼 노출 —
 *                   onImportDay(day.dayIndex) 로 부모(FolioMobile)에 알려 DayImportDialog 를 연다.
 */
function DayTab({
  days,
  canImportDay = false,
  onImportDay,
}: {
  days: DayPlan[];
  canImportDay?: boolean;
  onImportDay?: (dayIndex: number) => void;
}): JSX.Element {
  const [activeDay, setActiveDay] = useState(0);
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);
  const congestion = usePoiCongestion(selectedPoiId ?? undefined);
  const day = days[activeDay];
  if (!day) return <Box sx={{ p: '14px 16px', color: TEXT_MID }}>일정이 없습니다.</Box>;

  const bandGroups = groupPlanItemsByTimeBand(day.items);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: '6px', p: '12px 16px 0', overflowX: 'auto' }}>
        {days.map((d, i) => {
          const active = activeDay === i;
          return (
            <ButtonBase
              key={d.id}
              onClick={() => setActiveDay(i)}
              aria-label={`Day ${d.dayIndex}`}
              aria-pressed={active}
              sx={{
                flexShrink: 0,
                borderRadius: `${tokens.radius.xl}px`,
                px: `${tokens.spacing[4]}px`,
                py: `${tokens.spacing[1]}px`,
                fontSize: 12,
                border: `1px solid ${active ? BRAND : BORDER}`,
                bgcolor: active ? `${BRAND}1A` : c.paper[0][L],
                color: active ? BRAND : TEXT_MID,
                fontWeight: active ? 600 : 400,
                '&:focus-visible': {
                  outline: `2px solid ${BRAND}`,
                  outlineOffset: '2px',
                },
              }}
            >
              Day {d.dayIndex}
            </ButtonBase>
          );
        })}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: '12px 16px' }}>
        <Box sx={{ mb: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '3px' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_HI }}>
              {day.date ? `${day.date} — ` : ''}Day {day.dayIndex}
            </Typography>
            {canImportDay && (
              <ButtonBase
                onClick={() => onImportDay?.(day.dayIndex)}
                aria-label={`Day ${day.dayIndex} 내 폴리오로 가져오기`}
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: BRAND,
                  border: `1px solid ${BRAND}`,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '6px',
                  py: '2px',
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
                }}
              >
                ⭳ 가져오기
              </ButtonBase>
            )}
          </Box>
          {/* [목적] Day 날씨 표시 — T13 ADR-TR-6: day.weather 있으면 표시, 날짜가 있는데 예보가 없으면
                    "예보 없음" 안내(FlowLine A6) */}
          {day.weather ? (
            <MobileWeatherLabel weather={day.weather} />
          ) : (
            day.date && (
              <Typography sx={{ fontSize: 11, color: TEXT_LOW, mb: '3px' }}>예보 없음</Typography>
            )
          )}
          {/* [수정] 2026-09-18 QA(M-2): 값이 0 이면 "데이터 없음" 이므로 감춘다 —
              FolioPage.tsx 의 동일 분기와 짝. 근본 해결은 BE 생성 단계의 costEstimate 채우기. */}
          {day.totalCostEstimate > 0 && (
            <Typography
              sx={{
                fontSize: 11,
                color: BRAND,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              예상 ₩{day.totalCostEstimate.toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* T13: 밴드 그룹 타임라인 렌더
            [목적] 4밴드(오전/점심/오후/저녁) 헤더 + 아이템 — 매거진형 하루 서사 표현
            [이유] ADR-FGQ2·T13 — 기존 flat 렌더를 밴드 그룹으로 교체
            [사이드 이팩트] globalIdx 로 day 전체 기준 아이템 번호 유지
            [수정 시 고려사항] 빈 밴드 자동 숨김(groupMobilePlanItemsByBand 내부) */}
        {(() => {
          let globalIdx = 0;
          return bandGroups.map(({ band, items: bandItems }) => {
            const meta = BAND_META[band];
            const groupStart = globalIdx;
            globalIdx += bandItems.length;
            return (
              <Box
                key={band}
                component="section"
                aria-label={`${meta.label} 일정`}
                sx={{ mb: '12px' }}
              >
                {/* 밴드 헤더 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    mb: '8px',
                    pb: '6px',
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <Box component="span" aria-hidden="true" sx={{ fontSize: 12, lineHeight: 1 }}>
                    {meta.emoji}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: TEXT_LOW,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    }}
                  >
                    {meta.label}
                  </Typography>
                </Box>

                {/* 밴드 내 아이템 */}
                {bandItems.map((item, localIdx) => {
                  const i = groupStart + localIdx;
                  return (
                    <Box
                      key={item.id}
                      onClick={() => setSelectedPoiId(item.factInfo?.contentId ?? null)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TimelineItem item={item} index={i} />
                    </Box>
                  );
                })}
              </Box>
            );
          });
        })()}

        {/* 혼잡도 예측 카드 — 타임라인 아이템 클릭 시 표시
            [목적] 선택된 POI 의 30일 혼잡도 예측을 DayTab 하단에 인라인 표시
            [이유] WC-TOURAPI-QR-5-03 — 모바일에서 별도 화면 이동 없이 확인 가능하도록
            [사이드 이팩트] usePoiCongestion 재요청 발생
            [수정 시 고려사항] item.poiId 미포함 시 selectedPoiId=null → 미표시 (정상) */}
        {congestion.data && selectedPoiId && (
          <CongestionForecastCard poiId={selectedPoiId} forecast={congestion.data} />
        )}
      </Box>
    </Box>
  );
}

/**
 * [목적] 모바일 지도 탭 — Day 선택 + FolioMap(SDK/폴백/빈 상태) + 방문지 목록(마커 번호 매칭)
 * [이유] FlowLine A4(fd5c1e75): "카카오맵 연동" placeholder 박스 제거 — 실 좌표 기반 FolioMap 재사용
 *        (데스크톱 FolioPage.tsx 와 동일 컴포넌트, 순번 가짜 좌표 생성 없음).
 * [사이드 이팩트] 방문지 목록 클릭 시 focusedIndex 갱신 → FolioMap 핀/마커도 동일 인덱스로 하이라이트(양방향 연동).
 * [수정 시 고려사항] Day 전환 시 focusedIndex 를 null 로 리셋 — 이전 Day 의 인덱스가 새 Day 에 잘못 매칭되는 것 방지.
 */
function MapTab({ days }: { days: DayPlan[] }): JSX.Element {
  const [activeDay, setActiveDay] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const day = days[activeDay];
  if (!day) return <Box sx={{ p: '14px 16px', color: TEXT_MID }}>일정이 없습니다.</Box>;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: '6px', p: '12px 16px 0', overflowX: 'auto' }}>
        {days.map((d, i) => {
          const active = activeDay === i;
          return (
            <ButtonBase
              key={d.id}
              onClick={() => {
                setActiveDay(i);
                setFocusedIndex(null);
              }}
              aria-label={`Day ${d.dayIndex}`}
              aria-pressed={active}
              sx={{
                flexShrink: 0,
                borderRadius: `${tokens.radius.xl}px`,
                px: `${tokens.spacing[4]}px`,
                py: `${tokens.spacing[1]}px`,
                fontSize: 12,
                border: `1px solid ${active ? BRAND : BORDER}`,
                bgcolor: active ? `${BRAND}1A` : c.paper[0][L],
                color: active ? BRAND : TEXT_MID,
                fontWeight: active ? 600 : 400,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
              }}
            >
              Day {d.dayIndex}
            </ButtonBase>
          );
        })}
      </Box>

      <Box sx={{ height: 220, m: '12px 16px 0', flexShrink: 0 }}>
        <FolioMap day={day} focusedIndex={focusedIndex} onFocusItem={setFocusedIndex} />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: '12px 16px' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_HI, mb: '10px' }}>
          Day {day.dayIndex} 방문지 ({day.items.length}곳)
        </Typography>
        {day.items.map((item, i) => {
          const active = focusedIndex === i;
          return (
            <ButtonBase
              key={item.id}
              onClick={() => setFocusedIndex(i)}
              aria-label={`${item.title ?? '이름 없는 장소'} — 지도에서 보기`}
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: '10px',
                p: '9px 12px',
                bgcolor: active ? `${BRAND}0F` : c.paper[0][L],
                borderRadius: '10px',
                border: `1px solid ${active ? BRAND : BORDER}`,
                mb: '6px',
                textAlign: 'left',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: BRAND,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_HI }}>
                  {item.title ?? '(이름 없음)'}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TEXT_MID }}>
                  {item.location ? item.type : `${item.type} · 위치 정보 없음`}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * [목적] 모바일 무드 탭 — items[].imageUrl 실사진 콜라주(최대 6) + vibe 태그
 * [이유] FlowLine A5(88f19470): 하드코딩 MOODS(고정 이모지·색) 콜라주 제거 — 실제 방문지 사진으로 대체.
 *        사진이 하나도 없으면 vibe 태그만(기존 하단 섹션 유지) — graceful.
 * [사이드 이팩트] MOODS 상수는 이 함수의 유일한 소비처였으므로 함께 제거.
 * [수정 시 고려사항] alt 텍스트는 item.title(장소명) — 이미지 로딩 실패/스크린리더 모두 의미 있는 텍스트 확보.
 *                   loading="lazy" 기본 적용(§ 규칙: 이미지 lazy loading 기본).
 */
function MoodTab({ folio }: { folio: Folio }): JSX.Element {
  const days = folio.days ?? [];
  const photos = days
    .flatMap((d) => d.items)
    .filter((item): item is PlanItem & { imageUrl: string } => Boolean(item.imageUrl))
    .slice(0, 6);

  return (
    <Box sx={{ p: '14px 16px', height: '100%', overflowY: 'auto' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_HI, mb: '12px' }}>
        이 여행의 무드
      </Typography>
      {photos.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', mb: '20px' }}>
          {photos.map((item) => (
            <Box
              key={item.id}
              component="img"
              src={item.imageUrl}
              alt={item.title ?? '여행지 사진'}
              loading="lazy"
              sx={{
                width: '100%',
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                borderRadius: '12px',
                bgcolor: c.paper[2][L],
              }}
            />
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, color: TEXT_MID, mb: '20px' }}>
          아직 등록된 사진이 없어요.
        </Typography>
      )}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_HI, mb: '10px' }}>
        무드 키워드
      </Typography>
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {folio.vibe.length > 0 ? (
          folio.vibe.map((v) => (
            <Box
              key={v}
              component="span"
              sx={{
                fontSize: 11,
                bgcolor: `${BRAND}14`,
                color: BRAND,
                borderRadius: 99,
                px: '10px',
                py: '4px',
                fontWeight: 600,
              }}
            >
              #{v}
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: 12, color: TEXT_MID }}>무드 키워드가 없어요.</Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * [목적] 모바일 예산 탭 — T15 ADR-FGQ-OQ5/6 정합: 인당·0원 숨김·"약 N만원" 표기
 * [이유] T15: FolioBudgetPreview / FolioPage BudgetCategoryBar 와 동일 규칙 적용.
 *         ADR-FGQ-OQ5: budgetPerPerson(BE 직렬화 값) 있으면 "인당 약 N만원" 표시.
 *         ADR-FGQ-OQ6: Math.round(amt/10000)===0 인 카테고리 숨김. "약 N만원" 포맷(formatKrw).
 *         FlowLine A3(71e75fbb): BUDGET mock 폴백 제거 — budgetBreakdown null 이면 "예산 정보 없음" 안내.
 *         isEstimate=true 면 "추정치" 배지, perPerson=true 면 "1인 기준" 배지(FolioPage.tsx BudgetCategoryBar 와 동일 규칙).
 * [사이드 이팩트] formatKrw import(FolioBudgetPreview SoT) — "₩{amt.toLocaleString()}" 표기 제거.
 *                 BUDGET_CATEGORY_COLORS/BUDGET_CATEGORY_LABEL import(FolioPage.tsx SoT) 유지.
 *                 design-mock BUDGET import 제거 — 이 함수가 유일한 소비처였음.
 * [수정 시 고려사항] budgetBreakdown 키 추가 시 FolioPage.tsx BUDGET_CATEGORY_* 도 동기 갱신.
 *                   budgetPerPerson 은 BE 직렬화 값(folio.budgetPerPerson) — FE headcount 재계산 아님.
 */
function BudgetTab({ folio }: { folio: Folio }): JSX.Element {
  interface MobileSlice {
    cat: string;
    amt: number;
    color: string;
    pct: number;
  }

  // FlowLine A3: mock 폴백 제거 — budgetBreakdown 없으면 slices=[] 로 "예산 정보 없음" 분기
  // [수정] M6 P1(budget-structuring, packages/types): budgetBreakdown 에 perPerson·isEstimate boolean
  //        메타 필드가 추가되어 Object.entries(bd) 로 blind 순회하면 boolean 값이 "카테고리 금액"으로
  //        잘못 섞여 들어간다(타입 에러 + 런타임 오표시 위험) — BUDGET_CATEGORY_LABEL 의 5개 키만
  //        명시 순회하도록 근본 수정(FolioPage.tsx BudgetCategoryBar 와 동일 접근).
  const budgetBreakdown = folio.budgetBreakdown;
  const slices: MobileSlice[] = (() => {
    if (!budgetBreakdown) return [];
    // [수정 시 고려사항] BUDGET_CATEGORY_LABEL 은 Record<string,string> 로 선언돼 keyof 가 string 으로
    //                   넓혀지므로, bd 인덱싱에는 BudgetCategoryKey(5개 카테고리 키 유니온, boolean 메타
    //                   필드 perPerson/isEstimate 제외)를 사용한다(FolioPage.tsx BudgetCategoryKey 주석 참조).
    const categoryKeys = Object.keys(BUDGET_CATEGORY_LABEL) as Array<BudgetCategoryKey>;
    const entries = categoryKeys.map((key) => [key, budgetBreakdown[key] ?? 0] as const);
    const visible = entries.filter(([, amt]) => Math.round(amt / 10000) > 0);
    const total = visible.reduce((s, [, v]) => s + v, 0);
    if (total === 0) return [];
    // [수정: QA-P1-04] 카테고리별 비율 독립 반올림 시 합이 100%를 벗어날 수 있다 — 최대잔여법으로 보정
    // (FolioPage.tsx BudgetCategoryBar 와 동일 로직, distributePercentages 공유).
    const pcts = distributePercentages(visible.map(([, amt]) => amt));
    return visible.map(([key, amt], i) => ({
      cat: BUDGET_CATEGORY_LABEL[key] ?? key,
      amt,
      color: BUDGET_CATEGORY_COLORS[key] ?? BRAND,
      pct: pcts[i]!,
    }));
  })();

  const totalAmt = slices.reduce((s, b) => s + b.amt, 0);
  // T15 ADR-FGQ-OQ5: budgetPerPerson 있으면 "인당 약 N만원" 표시
  const perPersonLabel = folio.budgetPerPerson ? formatKrw(folio.budgetPerPerson) : null;
  const isEstimate = budgetBreakdown?.isEstimate === true;
  const isPerPersonAmounts = budgetBreakdown?.perPerson === true;

  return (
    <Box sx={{ p: '14px 16px', height: '100%', overflowY: 'auto' }}>
      {/* 총액 + 배지(추정치/1인 기준) + 인당 금액 헤더 */}
      <Box sx={{ textAlign: 'center', mb: '20px' }}>
        <Typography sx={{ fontSize: 11, color: TEXT_MID, mb: '4px' }}>총 예상 비용</Typography>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: TEXT_HI,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          }}
          aria-label={`총 예산 ${formatKrw(totalAmt)}`}
        >
          {formatKrw(totalAmt)}
        </Typography>
        {(isEstimate || isPerPersonAmounts) && (
          <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'center', mt: '6px' }}>
            {isEstimate && (
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: TEXT_MID,
                  border: `1px solid ${BORDER}`,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '6px',
                  py: '1px',
                }}
              >
                추정치
              </Box>
            )}
            {isPerPersonAmounts && (
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: BRAND,
                  border: `1px solid ${BRAND}`,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '6px',
                  py: '1px',
                }}
              >
                1인 기준
              </Box>
            )}
          </Box>
        )}
        {/* ADR-FGQ-OQ5: 인당 금액 — budgetPerPerson 있을 때만 */}
        {perPersonLabel && (
          <Typography
            sx={{ fontSize: 12, color: GREEN, mt: '4px' }}
            aria-label={`인당 ${perPersonLabel}`}
          >
            인당 {perPersonLabel}
          </Typography>
        )}
      </Box>

      {/* FlowLine A3: budgetBreakdown 없으면 고정 mock 대신 "예산 정보 없음" 안내 */}
      {!budgetBreakdown && (
        <Typography sx={{ fontSize: 12, color: TEXT_MID, textAlign: 'center', py: '16px' }}>
          예산 정보 없음
        </Typography>
      )}

      {slices.length > 0 && (
        <>
          {/* 분배 바 */}
          <Box
            role="img"
            aria-label={`예산 분배 바: ${slices.map((b) => `${b.cat} ${b.pct}%`).join(', ')}`}
            sx={{
              display: 'flex',
              height: 12,
              borderRadius: `${tokens.radius.s}px`,
              overflow: 'hidden',
              mb: '16px',
              gap: '1px',
            }}
          >
            {slices.map((b) => (
              <Box key={b.cat} sx={{ width: `${b.pct}%`, bgcolor: b.color }} />
            ))}
          </Box>

          {/* 카테고리 목록 — "약 N만원" 포맷 (ADR-FGQ-OQ6) */}
          {slices.map((b) => (
            <Box
              key={b.cat}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                p: '10px 0',
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }}
              />
              <Box component="span" sx={{ flex: 1, fontSize: 13, color: TEXT_HI }}>
                {b.cat}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  color: TEXT_HI,
                  fontWeight: 500,
                }}
              >
                {formatKrw(b.amt)}
              </Box>
              <Box
                component="span"
                sx={{ fontSize: 11, color: TEXT_LOW, width: 32, textAlign: 'right' }}
              >
                {b.pct}%
              </Box>
            </Box>
          ))}
        </>
      )}

      <Typography
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pt: '12px',
          fontSize: 12,
          color: TEXT_MID,
        }}
      >
        ※ 예상치이며 실제와 다를 수 있음
      </Typography>
    </Box>
  );
}

// [목적] FolioMobile 본체 — 커버 + 탭 + 콘텐츠 + AI 보정 바 + "조용한" 칩
// [이유] WC-TOURAPI-QR-5-02/05 — 모바일 폴리오 뷰에도 "조용한" 필터 칩 제공
// [사이드 이팩트] quiet 상태 → plan-generate 재생성 payload boolean flag (plan-generate Spec 확정 후 연결)
// [수정 시 고려사항] QUIET_CHIP_ENABLED=false 시 칩 disabled + Tooltip "준비 중" 표시
export function FolioMobile({ folio }: { folio: Folio }): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('day');
  // fe-folio-social-wire: 로컬 liked/forked state → 실 API 훅(useFolioLike/useForkFolio) 배선.
  // [G2-1, 2026-09-17] 계약 C1(folio.viewer) 반영 — 초기값을 folio.viewer?.liked/bookmarked 로 시드.
  const {
    liked,
    likeCount,
    toggle: toggleLike,
    isPending: likePending,
  } = useFolioLike(folio.id, folio.likeCount, folio.viewer?.liked);
  const {
    bookmarked,
    bookmarkCount,
    toggle: toggleBookmark,
  } = useFolioBookmark(folio.id, folio.bookmarkCount, folio.viewer?.bookmarked);
  const likeGate = useAuthGate('folio.like');
  const bookmarkGate = useAuthGate('folio.bookmark');
  // fork 는 useAuthGate 의 intent union 에 없어 데스크톱 ForkButton(FolioPage.tsx)과 동일하게
  // status==='guest' 분기로 직접 /login redirect 처리한다(ADR 없음, 기존 패턴 재사용).
  const { status, user } = useAuth();
  const fork = useForkFolio();
  // [목적] "조용한" 필터 토글 상태
  // [이유] WC-TOURAPI-QR-5-02 — 모바일 폴리오 뷰에서도 동일 칩 라인업 제공
  // [사이드 이팩트] 없음 (로컬 UI 상태)
  // [수정 시 고려사항] plan-generate Spec 확정 후 재생성 API payload 에 { quiet } 포함 필요
  const [quiet, setQuiet] = useState(false);
  const days = folio.days ?? [];
  const coverColor = folio.themeColor ?? BRAND;
  // FlowLine A9: 소유자 여부 — FolioPage.tsx FolioContent 와 동일 휴리스틱(헤더 주석 참조)
  const isOwner = folio.ownerUserId
    ? status === 'authenticated' && folio.ownerUserId === user?.id
    : status !== 'authenticated';
  // FlowLine A7: 폴리오 신고 다이얼로그 상태 + 게스트 가드
  const [folioReportOpen, setFolioReportOpen] = useState(false);
  const folioReportGate = useAuthGate('report.create');
  // FlowLine A8: Day 가져오기 다이얼로그 상태 — 지도/Day 탭에서 마지막으로 본 Day 인덱스 기준(기본 0)
  const [dayImportOpen, setDayImportOpen] = useState(false);
  const [importDayIndex, setImportDayIndex] = useState(1);
  // [목적] FlowLine dcc2c3cc FE 과업1: AI 보정 대상 Day 선택 상태 — 데스크톱(FolioEditPage)과 달리
  //        모바일은 탭이 Day/지도/무드/예산/댓글이라 "활성 Day 탭"이 없어 별도 선택 상태가 필요.
  // [이유] AI 보정 바가 모든 탭에서 공통 노출되므로 보정 대상 Day 를 사용자가 직접 고를 수 있어야 함.
  // [사이드 이팩트] 없음(로컬 UI 상태) — days 가 바뀌어도 기존 선택값 유지, 없는 인덱스면 첫 Day 로 폴백.
  // [수정 시 고려사항] days.length===1 이면 선택 UI 자체를 숨김(RefineDaySelect 미노출).
  const [refineDayIndex, setRefineDayIndex] = useState(1);
  const refine = useFolioRefine(folio.id);

  const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
    { id: 'day', label: 'Day' },
    { id: 'map', label: '지도' },
    { id: 'mood', label: '무드' },
    { id: 'budget', label: '예산' },
    { id: 'comments', label: '댓글' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: BG1 }}>
      {/* 커버 */}
      <Box sx={{ bgcolor: coverColor, p: '14px 16px 0', position: 'relative', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px' }}>
          <ButtonBase
            onClick={() => navigate(-1)}
            aria-label="뒤로"
            sx={{
              bgcolor: HEADER_CHIP_BG,
              borderRadius: '8px',
              px: '10px',
              py: '4px',
              color: '#fff',
              fontSize: 13,
            }}
          >
            ← 뒤로
          </ButtonBase>
          <Box sx={{ flex: 1 }} />
          {/* FlowLine A2: PDF 내보내기 — 요청/폴링/자동 다운로드/재시도 */}
          <FolioExportButton
            folioId={folio.id}
            sx={{
              bgcolor: HEADER_CHIP_BG,
              borderRadius: '8px',
              px: '10px',
              py: '4px',
              color: '#fff',
              fontSize: 12,
            }}
          />
          {/* FlowLine A1: 공유 — navigator.share/클립보드 복사 + private 전환 안내 */}
          <FolioShareButton
            folio={folio}
            isOwner={isOwner}
            sx={{
              bgcolor: HEADER_CHIP_BG,
              borderRadius: '8px',
              px: '10px',
              py: '4px',
              color: '#fff',
              fontSize: 12,
            }}
          >
            공유
          </FolioShareButton>
          {/* FlowLine A9: 편집 버튼은 소유자에게만 노출 */}
          {isOwner && (
            <ButtonBase
              onClick={() => navigate(`/folio/${folio.id}/edit`)}
              aria-label="편집"
              sx={{
                bgcolor: HEADER_CHIP_BG,
                borderRadius: '8px',
                px: '10px',
                py: '4px',
                color: '#fff',
                fontSize: 12,
              }}
            >
              ✎ 편집
            </ButtonBase>
          )}
          {/* FlowLine A7: 폴리오 신고 — 비소유자에게만, 게스트는 useAuthGate('report.create') 로 가드 */}
          {!isOwner && (
            <ButtonBase
              onClick={() => folioReportGate.guard(() => setFolioReportOpen(true))}
              aria-label="폴리오 신고"
              sx={{
                bgcolor: HEADER_CHIP_BG,
                borderRadius: '8px',
                px: '10px',
                py: '4px',
                color: '#fff',
                fontSize: 12,
              }}
            >
              신고
            </ButtonBase>
          )}
        </Box>
        <Box sx={{ mb: '12px' }}>
          {/* T15: 작성자 author.handle 표시. viewCount 표시(G8).
              2026-09-18 QA(M-4): 부재 시 @slug 폴백 → 익명 라벨(folioAuthorLabel). slug 는 뒤에 #slug 로 별도 표기. */}
          <Typography
            sx={{ fontSize: 10, color: 'rgba(255,255,255,.7)', mb: '4px', fontWeight: 500 }}
          >
            {folioAuthorLabel(folio.author)}
            {' · '}
            {/* [수정: 2026-09-20] 조회수 글리프 '👁'(이모지) → LineIcon 'eye'.
                데스크톱 FolioPage 와 동일 조치 — 이모지는 플랫폼별 컬러 그림이라 히어로 위 반투명
                흰 캡션 줄에서 혼자 튀었다. 아이콘은 currentColor 상속이라 같은 흰색 톤을 따른다. */}
            {folio.viewCount != null && (
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', mr: '4px' }}
              >
                <LineIcon name="eye" size={11} aria-hidden />
                <Box component="span" sx={MOBILE_VISUALLY_HIDDEN_SX}>
                  조회수
                </Box>
                {folio.viewCount.toLocaleString()}
                {' ·'}
              </Box>
            )}
            {'#'}
            {folio.slug}
          </Typography>
          <Typography
            sx={{ fontSize: 17, fontWeight: 700, color: '#fff', lineHeight: 1.35, mb: '6px' }}
          >
            {folio.title}
          </Typography>
          {folio.subtitle && (
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.8)' }}>
              {folio.subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{ display: 'flex', gap: '10px', alignItems: 'center', pb: '14px', flexWrap: 'wrap' }}
        >
          {/* fe-folio-social-wire: useFolioLike 실배선 — 낙관적 업데이트 + guest 가드 */}
          <ButtonBase
            onClick={() => likeGate.guard(toggleLike)}
            disabled={likePending}
            aria-label={liked ? '좋아요 취소' : '좋아요'}
            aria-pressed={liked}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              bgcolor: HEADER_CHIP_BG,
              borderRadius: `${tokens.radius.s}px`,
              px: `${tokens.spacing[2]}px`,
              py: `${tokens.spacing[1]}px`,
              color: c.paper[0][L],
              fontSize: 12,
              opacity: likePending ? 0.7 : 1,
              '&:focus-visible': { outline: `2px solid ${c.paper[0][L]}`, outlineOffset: '2px' },
            }}
          >
            <Box component="span">{liked ? '♥' : '♡'}</Box>
            <Box component="span">{likeCount}</Box>
          </ButtonBase>
          {/* fe-folio-social-wire: useFolioBookmark 실배선 */}
          <ButtonBase
            onClick={() => bookmarkGate.guard(toggleBookmark)}
            aria-label={bookmarked ? '북마크 취소' : '북마크'}
            aria-pressed={bookmarked}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              bgcolor: HEADER_CHIP_BG,
              borderRadius: `${tokens.radius.s}px`,
              px: `${tokens.spacing[2]}px`,
              py: `${tokens.spacing[1]}px`,
              color: c.paper[0][L],
              fontSize: 12,
              '&:focus-visible': { outline: `2px solid ${c.paper[0][L]}`, outlineOffset: '2px' },
            }}
          >
            <Box component="span">{bookmarked ? '🔖' : '☆'}</Box>
            <Box component="span">{bookmarkCount}</Box>
          </ButtonBase>
          {/* [수정: 디자인 개편] 댓글 수 카운터 추가 — 좋아요/북마크와 같은 보조 카운터 묶음으로 표시 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              bgcolor: HEADER_CHIP_BG,
              borderRadius: `${tokens.radius.s}px`,
              px: `${tokens.spacing[2]}px`,
              py: `${tokens.spacing[1]}px`,
              color: c.paper[0][L],
              fontSize: 12,
            }}
            aria-label={`댓글 ${folio.commentCount}개`}
          >
            <Box component="span">💬</Box>
            <Box component="span">{folio.commentCount}</Box>
          </Box>
          {/* fe-folio-social-wire: useForkFolio 실배선 — 데스크톱 ForkButton과 동일 guest 분기 */}
          {/* [수정: 디자인 개편] Fork는 이 줄에서 제거하고 아래에 전체폭 주 액션으로 따로 배치(folio.forkCount 는 새 버튼 아래 배지로 이동) */}

          {/* "조용한" 칩 — feature flag + chip.quiet 토큰
              [목적] 모바일 폴리오 커버 액션 영역에 "조용한" 필터 칩 노출
              [이유] WC-TOURAPI-QR-5-02/05 — 채팅/데스크톱 폴리오와 동일한 칩 라인업 유지
              [사이드 이팩트] quiet 상태 토글 — plan-generate Spec 확정 후 재생성 API payload 에 포함
              [수정 시 고려사항] 커버 배경색 위이므로 disabled 시 반투명 흰색 계열 유지 */}
          {(() => {
            const isDisabled = !QUIET_CHIP_ENABLED;
            const chipEl = (
              <ButtonBase
                onClick={() => !isDisabled && setQuiet((v) => !v)}
                aria-label="조용한 여행 필터 활성화"
                aria-pressed={quiet}
                disabled={isDisabled}
                sx={{
                  bgcolor: isDisabled
                    ? 'rgba(255,255,255,.15)'
                    : quiet
                      ? QUIET_BG_ACTIVE
                      : QUIET_BG,
                  border: `1px solid ${
                    isDisabled ? 'rgba(255,255,255,.3)' : quiet ? QUIET_BG_ACTIVE : QUIET_BORDER
                  }`,
                  color: isDisabled ? 'rgba(255,255,255,.5)' : quiet ? QUIET_FG_ACTIVE : QUIET_FG,
                  borderRadius: `${tokens.radius.s}px`,
                  px: `${tokens.spacing[2]}px`,
                  py: `${tokens.spacing[1]}px`,
                  fontSize: 11,
                  fontWeight: quiet ? 700 : 500,
                  opacity: isDisabled ? 0.6 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: `all ${tokens.motion.fast.duration}ms`,
                  '&:focus-visible': {
                    outline: `2px solid ${c.paper[0][L]}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                🤫 조용한
              </ButtonBase>
            );
            return isDisabled ? (
              <Tooltip title="준비 중 — 활용 신청 후 활성화" arrow placement="top">
                <span>{chipEl}</span>
              </Tooltip>
            ) : (
              chipEl
            );
          })()}
        </Box>

        {/* [수정: 디자인 개편] Fork를 이 전체폭 주 액션으로 따로 배치 — 좋아요/북마크/댓글수와 달리 이 폴리오의 핵심 행동이라
            큰 파란 버튼으로 구분한다. */}
        <ButtonBase
          onClick={() => {
            if (status === 'guest') {
              navigate(`/login?returnTo=${encodeURIComponent(`/folio/${folio.id}`)}`);
              return;
            }
            fork.mutate(
              { folioId: folio.id },
              { onSuccess: (newFolio) => navigate(`/folio/${newFolio.id}`) },
            );
          }}
          disabled={fork.isPending}
          aria-label={
            status === 'guest'
              ? '내 폴리오로 가져오기 — 로그인 후 이용 가능'
              : '내 폴리오로 가져오기'
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            mt: '10px',
            py: '13px',
            bgcolor: fork.isPending ? c.paper[2][L] : BRAND,
            color: fork.isPending ? c.ink.low[L] : '#fff',
            borderRadius: `${tokens.radius.m}px`,
            fontSize: 14,
            fontWeight: 700,
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
          }}
        >
          {fork.isPending ? (
            <CircularProgress size={14} sx={{ color: 'inherit' }} />
          ) : (
            `⑂ 내 폴리오로 가져오기 (${folio.forkCount})`
          )}
        </ButtonBase>

        {/* 탭 */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: 'rgba(0,0,0,.15)',
            borderRadius: '10px 10px 0 0',
            overflow: 'hidden',
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <ButtonBase
                key={t.id}
                onClick={() => setTab(t.id)}
                aria-label={`탭 ${t.label}`}
                aria-current={active ? 'page' : undefined}
                sx={{
                  flex: 1,
                  p: '9px 4px',
                  fontSize: 12,
                  fontWeight: active ? 700 : 400,
                  bgcolor: active ? '#fff' : 'transparent',
                  color: active ? coverColor : 'rgba(255,255,255,.8)',
                  borderRadius: active ? '8px 8px 0 0' : 0,
                }}
              >
                {t.label}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* 탭 콘텐츠 */}
      <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: BG1 }}>
        {tab === 'day' && (
          <DayTab
            days={days}
            canImportDay={!isOwner && status === 'authenticated'}
            onImportDay={(dayIndex) => {
              setImportDayIndex(dayIndex);
              setDayImportOpen(true);
            }}
          />
        )}
        {tab === 'map' && <MapTab days={days} />}
        {tab === 'mood' && <MoodTab folio={folio} />}
        {tab === 'budget' && <BudgetTab folio={folio} />}
        {tab === 'comments' && (
          <Box sx={{ height: '100%', overflowY: 'auto', p: '14px 16px' }}>
            <FolioCommentSection folioId={folio.id} />
          </Box>
        )}
      </Box>

      {/* AI 보정 바 — FlowLine dcc2c3cc FE 과업1: 소유자에게만 노출, RefineInputBar/Dialog 실배선 */}
      {isOwner &&
        days.length > 0 &&
        (() => {
          // [목적] 선택된 refineDayIndex 가 현재 days 목록에 없으면(초기값 1 이 실제 첫 Day.dayIndex 와
          //        다를 수 있음) 첫 Day 로 안전 폴백 — noUncheckedIndexedAccess 대응으로 days[0] 를
          //        블록 진입 시(days.length>0 보장된 시점) 한 번만 단언.
          const fallbackDayIndex = days[0]!.dayIndex;
          const effectiveDayIndex = days.some((d) => d.dayIndex === refineDayIndex)
            ? refineDayIndex
            : fallbackDayIndex;
          return (
            <Box
              sx={{
                p: '8px 16px 10px',
                bgcolor: c.paper[0][L],
                borderTop: `1px solid ${BORDER}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                flexShrink: 0,
              }}
            >
              {days.length > 1 && (
                <Select
                  value={effectiveDayIndex}
                  onChange={(e) => setRefineDayIndex(Number(e.target.value))}
                  size="small"
                  aria-label="AI 보정 대상 Day 선택"
                  sx={{ fontSize: 11, alignSelf: 'flex-start', minWidth: 90 }}
                >
                  {days.map((d) => (
                    <MenuItem key={d.id} value={d.dayIndex}>
                      Day {d.dayIndex}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <RefineInputBar
                key={refine.resetKey}
                onSubmit={(instruction) =>
                  refine.propose(instruction, { type: 'day', dayIndex: effectiveDayIndex })
                }
                isSubmitting={refine.isProposing}
              />
            </Box>
          );
        })()}

      {/* fe-folio-social-wire: 좋아요/북마크 게스트 가드 모달 — 진입점별 인스턴스(AD-4) */}
      <LoginPromptModal
        open={likeGate.isPromptOpen}
        intent={likeGate.pendingIntent}
        onClose={likeGate.closePrompt}
      />
      <LoginPromptModal
        open={bookmarkGate.isPromptOpen}
        intent={bookmarkGate.pendingIntent}
        onClose={bookmarkGate.closePrompt}
      />
      {/* FlowLine A7: 폴리오 신고 다이얼로그 + 게스트 가드 모달 */}
      <LoginPromptModal
        open={folioReportGate.isPromptOpen}
        intent={folioReportGate.pendingIntent}
        onClose={folioReportGate.closePrompt}
      />
      {folioReportOpen && (
        <ReportDialog
          targetType="folio"
          targetId={folio.id}
          open
          onClose={() => setFolioReportOpen(false)}
        />
      )}
      {/* FlowLine A8: Day 가져오기 모달 — DayTab "가져오기" 클릭 시 선택된 Day 기준 */}
      <DayImportDialog
        open={dayImportOpen}
        onClose={() => setDayImportOpen(false)}
        sourceFolioId={folio.id}
        sourceDayIndex={importDayIndex}
      />
      {/* FlowLine dcc2c3cc FE 과업1: AI 보정 제안 미리보기 — RefineInputBar 제출 시 open */}
      <RefineProposalDialog
        open={refine.open}
        proposal={refine.proposal}
        isProposing={refine.isProposing}
        proposeErrorMessage={refine.proposeErrorMessage}
        isApplying={refine.isApplying}
        applyErrorMessage={refine.applyErrorMessage}
        onApply={refine.apply}
        onCancel={refine.cancel}
      />
    </Box>
  );
}
